import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useClienteAuth } from '../context/ClienteAuthContext';
import { supabase } from '../lib/supabase';
import { AlertCircle, Loader2 } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import productexLogo from '../components/layout/productex-logo.png';

const UnifiedLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCheckingUser, setIsCheckingUser] = useState(false);

  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { login: clienteLogin } = useClienteAuth();

  // Verificar si ya hay sesiones activas
  useEffect(() => {
    const checkExistingSessions = async () => {
      setIsCheckingUser(true);
      
      try {
        // Verificar si hay sesión de empleado
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          navigate('/');
          return;
        }

        // Verificar si hay sesión de cliente
        const clienteToken = localStorage.getItem('cliente_token');
        if (clienteToken) {
          const { data } = await supabase.rpc('validate_cliente_session', {
            p_token: clienteToken
          });
          
          if (data && data.length > 0 && data[0].is_valid) {
            navigate('/cliente');
            return;
          } else {
            localStorage.removeItem('cliente_token');
          }
        }
      } catch (error) {
        console.error('Error checking sessions:', error);
      } finally {
        setIsCheckingUser(false);
      }
    };

    checkExistingSessions();
  }, [navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      console.log('🔐 Intentando login con:', email);
      
      // Primero intentar autenticar como empleado
      console.log('👤 Intentando autenticación como empleado...');
      const authResult = await signIn(email, password);
      console.log('👤 Resultado empleado:', authResult);
      
      if (authResult && !authResult.error) {
        console.log('✅ Empleado autenticado');
        return;
      }

      // Si no es empleado, intentar como cliente
      console.log('👥 Intentando autenticación como cliente...');
      const clienteResult = await clienteLogin(email, password);
      console.log('👥 Resultado cliente:', clienteResult);
      
      if (clienteResult) {
        console.log('✅ Cliente autenticado, redirigiendo a /cliente');
        navigate('/cliente');
        return;
      }

      // Si ninguno funciona, mostrar error
      console.log('❌ Ninguna autenticación funcionó');
      setError('Email o contraseña incorrectos');
      
    } catch (error) {
      console.error('🚨 Login error:', error);
      setError('Ocurrió un error al intentar iniciar sesión');
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-white mx-auto mb-4" />
          <p className="text-white">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-black px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img
            src={productexLogo}
            alt="Productex"
            className="h-10 w-auto object-contain mx-auto"
          />
        </div>
        
        <div className="bg-white dark:bg-gray-800 p-8 rounded-lg shadow-md">
          <h2 className="text-center text-2xl font-extrabold text-gray-900 dark:text-white">
            Iniciar sesión
          </h2>
          
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input
                label="Email"
                id="email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="tu@email.com"
              />
              
              <Input
                label="Contraseña"
                id="password"
                name="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <AlertCircle className="h-5 w-5 text-red-400 dark:text-red-300" aria-hidden="true" />
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                      Error de inicio de sesión
                    </h3>
                    <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                      <p>{error}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? 'Iniciando sesión...' : 'Iniciar sesión'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLogin;
